import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import icMobile from "@/assets/images/console/icMobile.svg";
import icMobileOn from "@/assets/images/console/icMobileOn.svg";
import icPc from "@/assets/images/console/icPc.svg";
import icPcOn from "@/assets/images/console/icPcOn.svg";
import LoadingSpinner from "@/components/common/common/LoadingSpinner";
import NoData from "@/components/common/common/Nodata";
import Pagination from "@/components/console/common/Pagination";
import Tabs from "@/components/console/common/Tabs";
import Checkbox from "@/components/console/form/Checkbox";
import SearchInput from "@/components/console/form/SearchInput";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { IPopupListParams, popupTypes } from "@/constants/common/listParams";
import usePagination from "@/hooks/common/usePagination";
import { useUrlParams } from "@/hooks/common/useUrlParams";
import useCheckboxList from "@/hooks/console/useCheckboxList";
import { useDelPopup, useGetPopupList, usePostPopupState } from "@/service/console/popup";
import { usePopupStore } from "@/store/common/usePopupStore";
import { makeIntComma } from "@/utils/numberUtils";

import PopupPop from "./-components/PopupPop";

const schema = z.object({
    searchtxt: z.string().optional(),
});

type SearchValues = z.infer<typeof schema>;

interface Item {
    idx: number;
    p_title: string;
    p_s_date: string;
    p_e_date: string;
    p_width_size: number;
    p_height_size: number;
    p_one_day: string[];
    p_open: string[];
}

export default function PopupList() {
    const [items, setItems] = useState<Item[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const { checkedNum, allCheck, setCheckList, checkedList, setCheckedList, handleAllCheck, handleCheck } =
        useCheckboxList();
    const [totalPages, setTotalPages] = useState(1);
    // URL 파라미터 관리
    const { params, resetParams, updateParams } = useUrlParams<IPopupListParams>({
        type: { defaultValue: "P", type: "string", validValues: popupTypes },
        page: { defaultValue: 1, type: "number" },
        searchtxt: { defaultValue: "", type: "string" },
    });
    const [tabOn, setTabOn] = useState(params.type === "P" ? 0 : 1);
    const { currentPage, pages, setCurrentPage } = usePagination({ totalPages, initialPage: params.page });
    const { setValue, register, control } = useForm<SearchValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            searchtxt: params.searchtxt,
        },
    });
    const searchFilterValues = useWatch({ control });
    const {
        data: configData,
        isLoading: isInitialLoading,
        refetch,
        isRefetching,
    } = useGetPopupList(params.type.toString(), params.page.toString(), params.searchtxt.toString());
    const postPopupStateMutation = usePostPopupState();
    const delPopupMutation = useDelPopup();
    const [detailPop, setDetailPop] = useState<number | null>(null);
    const [createPop, setCreatePop] = useState(false);
    const { setLoadingPop, setConfirmPop } = usePopupStore();

    // PC, Mobile 탭
    const tabList = [
        <div key="tab_pc" className="flex items-center justify-center gap-[10px]">
            <img src={tabOn === 0 ? icPcOn : icPc} />
            <span>팝업 - PC</span>
        </div>,
        <div key="tab_mo" className="flex items-center justify-center gap-[10px]">
            <img src={tabOn === 1 ? icMobileOn : icMobile} />
            <span>팝업 - Mobile</span>
        </div>,
    ];

    // 파라미터 초기화
    useEffect(() => {
        resetParams();
        setCurrentPage(1);
        setValue("searchtxt", "");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 탭 변경 시
    const handleChangeTab = (idx: number) => {
        updateParams({ type: idx === 0 ? "P" : "M" });
        setCurrentPage(1);
    };

    // 파라미터 상태 동기화
    useEffect(() => {
        setTabOn(params.type === "P" ? 0 : 1);
        setValue("searchtxt", params.searchtxt);
    }, [params.type, params.searchtxt, setTabOn, setValue]);

    // currentPage 변경 시 URL 파라미터 업데이트
    const handleChangeCurrentPage = (page: number) => {
        updateParams({
            page: page,
        });
        setCurrentPage(page);
    };

    // 검색 하기
    const handleSearch = () => {
        console.log(1);
        const searchTxtValue = searchFilterValues.searchtxt || "";

        updateParams({
            type: tabOn === 0 ? "P" : "M",
            page: 1,
            searchtxt: searchTxtValue,
        });
        setCurrentPage(1);
    };

    // 데이터 수정,삭제 중일 때 로딩 팝업 표시
    useEffect(() => {
        const isLoading = postPopupStateMutation.isPending || delPopupMutation.isPending;
        setLoadingPop(isLoading, true);
        return () => setLoadingPop(false);
    }, [postPopupStateMutation.isPending, delPopupMutation.isPending, setLoadingPop]);

    // 팝업 목록 조회
    useEffect(() => {
        if (configData) {
            const data = configData.data;
            setItems(data.popup_list);
            setTotalPages(data.last_page);
            setTotalCount(data.total_count);
        } else {
            setItems([]);
            setTotalPages(1);
            setTotalCount(0);
        }
    }, [configData]);

    // 리스트 idx 값만 배열로 (전체 체크박스리스트 만들기)
    useEffect(() => {
        setCheckList(items.length > 0 ? items.map(item => item.idx) : []);
        setCheckedList([]);
    }, [items, setCheckList, setCheckedList]);

    // 노출여부 변경 확인
    const handleConfirmChangeState = (checked: boolean) => {
        if (checkedList.length > 0) {
            setConfirmPop(true, `선택한 팝업을 ${checked ? "노출" : "중단"} 하시겠습니까?`, 2, () =>
                handleChangeState(checked),
            );
        } else {
            setConfirmPop(true, "팝업을 선택해주세요.", 1);
        }
    };

    // 노출여부 변경하기
    const handleChangeState = (checked: boolean) => {
        const body = {
            idx: checkedList,
            p_open: checked ? "Y" : "N",
        };
        postPopupStateMutation.mutate(body, {
            onSuccess: () => {
                refetch();
            },
        });
    };

    // 삭제 확인
    const handleConfirmDelete = () => {
        if (checkedList.length > 0) {
            setConfirmPop(true, "삭제하시겠습니까?", 2, () => handleDelete());
        } else {
            setConfirmPop(true, "팝업을 선택해주세요.", 1);
        }
    };

    // 삭제하기
    const handleDelete = () => {
        const body = { idx: checkedList };
        delPopupMutation.mutate(body, {
            onSuccess: () => {
                setConfirmPop(true, "삭제되었습니다.", 1);
                refetch();
            },
        });
    };

    return (
        <>
            <Tabs list={tabList} activeIdx={tabOn} handleClick={handleChangeTab} />
            <div className="rounded-[8px] bg-white p-[36px_40px] shadow-[0_0_8px_0_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-[12px]">
                        <p className="text-[24px] font-[600]">전체</p>
                        <p className="font-[500] text-console">총 {makeIntComma(totalCount)}개</p>
                    </div>
                    <SearchInput {...register("searchtxt")} handleClick={handleSearch} />
                </div>
                <div className="mt-[20px] flex items-center justify-between rounded-[5px] bg-[#dce1ec] p-[20px_30px]">
                    <div className="flex items-center gap-[12px]">
                        <Checkbox checked={allCheck} onChange={e => handleAllCheck(e.currentTarget.checked)} />
                        <p className="text-[18px] font-[500]">
                            선택한 팝업 총 <span className="text-console">{makeIntComma(checkedNum)}</span>개
                        </p>
                        <div className="flex gap-[4px]">
                            <button
                                type="button"
                                className="h-[32px] w-[64px] rounded-[5px] bg-[#3F424F] text-[14px] font-[500] text-white"
                                onClick={() => handleConfirmChangeState(true)}
                            >
                                노출
                            </button>
                            <button
                                type="button"
                                className="h-[32px] w-[64px] rounded-[5px] bg-[#ECECEC] text-[14px] font-[500] text-[#666]"
                                onClick={() => handleConfirmChangeState(false)}
                            >
                                중단
                            </button>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="h-[30px] rounded-[5px] bg-[#ff5463] px-[10px] text-[14px] font-[500] text-white"
                        onClick={handleConfirmDelete}
                    >
                        삭제
                    </button>
                </div>
                <table>
                    <colgroup>
                        <col width="80px" />
                        <col width="10%" />
                        <col width="auto" />
                        <col width="auto" />
                        <col width="20%" />
                        <col width="12%" />
                    </colgroup>
                    <thead>
                        <tr className="border-b border-[#ddd] bg-[#fbfbfc] text-[18px] text-[#666]">
                            <th className="py-[15px] font-[500]"></th>
                            <th className="py-[15px] font-[500]">번호</th>
                            <th className="py-[15px] font-[500]">제목</th>
                            <th className="py-[15px] font-[500]">기간</th>
                            <th className="py-[15px] font-[500]">팝업창전체크기 / 1일여부</th>
                            <th className="py-[15px] font-[500]">사용여부</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isInitialLoading || isRefetching ? (
                            <tr>
                                <td colSpan={6} className="py-[50px]">
                                    <LoadingSpinner console />
                                </td>
                            </tr>
                        ) : items.length > 0 ? (
                            items.map((item, i) => (
                                <tr
                                    key={`board_${i}`}
                                    className="border-b border-[#ddd] text-center text-[#666] hover:bg-[#f7f6fb]"
                                >
                                    <td>
                                        <Checkbox
                                            checked={checkedList.includes(item.idx)}
                                            onChange={e => handleCheck(e.currentTarget.checked, item.idx)}
                                        />
                                    </td>
                                    <td className="p-[15px_5px]">{makeIntComma(item.idx)}</td>
                                    <td className="p-[15px_5px] text-left">
                                        <Dialog
                                            open={detailPop === i}
                                            onOpenChange={() => {
                                                if (detailPop === i) setDetailPop(null);
                                                else setDetailPop(i);
                                            }}
                                        >
                                            <DialogTrigger asChild>
                                                <button
                                                    type="button"
                                                    className="truncate font-[500] text-[#222] hover:underline"
                                                >
                                                    {item.p_title}
                                                </button>
                                            </DialogTrigger>
                                            {detailPop === i && (
                                                <PopupPop
                                                    handleClose={() => setDetailPop(null)}
                                                    open={detailPop === i}
                                                    handleComplete={() => refetch()}
                                                    type={tabOn === 0 ? "P" : "M"}
                                                    idx={item.idx}
                                                />
                                            )}
                                        </Dialog>
                                    </td>
                                    <td className="p-[15px_5px] text-[14px] text-[#999]">
                                        {item.p_s_date && item.p_e_date ? `${item.p_s_date} ~ ${item.p_e_date}` : "-"}
                                    </td>
                                    <td className="p-[15px_5px]">
                                        {`${makeIntComma(item.p_width_size)} X ${makeIntComma(item.p_height_size)}`} /{" "}
                                        {item.p_one_day[1]}
                                    </td>
                                    <td
                                        className={`p-[15px_5px] ${item.p_open[0] === "Y" ? "text-console" : "text-[#ff5463]"}`}
                                    >
                                        {item.p_open[1]}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr className="border-b border-[#ddd]">
                                <td colSpan={6}>
                                    <NoData />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="relative mt-[45px] flex justify-center px-[260px]">
                    {totalCount > 0 && (
                        <Pagination
                            totalPages={totalPages}
                            currentPage={currentPage}
                            pages={pages}
                            handleChangePage={handleChangeCurrentPage}
                        />
                    )}
                    <Dialog open={createPop} onOpenChange={setCreatePop}>
                        <DialogTrigger asChild>
                            <button
                                type="button"
                                className="absolute right-0 top-1/2 h-[50px] w-[240px] -translate-y-1/2 rounded-[5px] bg-[#192030] text-center text-[18px] font-[500] text-white"
                            >
                                팝업 등록
                            </button>
                        </DialogTrigger>
                        <PopupPop
                            handleClose={() => {
                                setCreatePop(false);
                            }}
                            open={createPop}
                            handleComplete={() => refetch()}
                            type={tabOn === 0 ? "P" : "M"}
                        />
                    </Dialog>
                </div>
            </div>
        </>
    );
}
